import { Location } from "gen/company/models_pb"
import { ReactNode, useMemo } from "react"
import { UUID } from "shared-ts/classes/uuid"
import { Chip } from "shared-web/components/base/informational/Chip"
import {
  CustomItem,
  useLocalSearchPopoverSelect,
  UseLocalSearchPopoverSelectProps,
  UseLocalSearchPopoverSelectRes,
} from "shared-web/components/composite/popover"

import { useListLocationsForCompanyQuery } from "features/api/company"

interface UseLocationsPopoverSelectProps
  extends Pick<
    UseLocalSearchPopoverSelectProps<Location>,
    "selected" | "onSelect" | "defaultSelected"
  > {
  filter?: (location: Location) => boolean
  customItems?: CustomItem[]
}

type UseLocationsPopoverSelectRes = UseLocalSearchPopoverSelectRes<Location>

function useLocationsPopoverSelect({
  filter,
  customItems,
  ...otherProps
}: UseLocationsPopoverSelectProps): UseLocationsPopoverSelectRes {
  const { data } = useListLocationsForCompanyQuery({ filterActive: true })

  const sortedLocations = useMemo(() => {
    const locations = data?.locations ?? []
    const filtered = filter ? locations.filter(filter) : locations
    return [...filtered].sort((a, b) => a.prefix.localeCompare(b.prefix))
  }, [data?.locations, filter])

  return useLocalSearchPopoverSelect({
    items: sortedLocations,
    keyExtractor,
    labelExtractor,
    accessoryExtractor,
    customItems,
    ...otherProps,
  })
}

function keyExtractor(location: Location): string {
  return UUID.idString(location.id)
}

function labelExtractor(location: Location): string {
  return location.name
}

function accessoryExtractor(location: Location): ReactNode {
  return <Chip color="gray" text={location.prefix} />
}

export default useLocationsPopoverSelect
